import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Skeleton } from "@app/components/shared/Skeleton";
import {
  fetchAssistantDashboard,
  sendAssistantMessage,
  type AiAssistantDashboard,
  type AiAssistantMessage,
  type AiAssistantTemplate,
  type AiAssistantSummary,
} from "@app/services/aiAssistantApi";
import type { PermissionProfile } from "@app/services/aiSupervisorApi";
import "./aiAssistant.css";

const DEFAULT_ROLE_ID = "admin";
const DEFAULT_PLAN_ID = "enterprise";
const EMPTY_SUMMARY: AiAssistantSummary = {
  totalConversations: 0,
  activeConversations: 0,
  totalTokens24h: 0,
  lastInteractionAt: null,
  warnings: [],
};

function formatRelativeTime(value?: string | null): string {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60_000);
  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? "s" : ""} ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  // Use fixed format independent of browser/OS locale: YYYY-MM-DD HH:mm
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  // Use fixed format independent of browser/OS locale: YYYY-MM-DD HH:mm
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

function MessageBubble({ message }: { message: AiAssistantMessage }) {
  const isUser = message.role === "user";
  const contentLines = useMemo(() => {
    if (!message.content) return [""];
    return message.content.split(/\n{2,}/g);
  }, [message.content]);

  return (
    <div className={`ai-message ${isUser ? "ai-message--user" : "ai-message--assistant"}`}>
      <div className="ai-message__header">
        <span className="ai-message__role">{isUser ? "You" : "Assistant"}</span>
        {message.createdAt && (
          <span className="ai-message__timestamp">{formatDateTime(message.createdAt)}</span>
        )}
      </div>
      <div className="ai-message__content">
        {contentLines.map((block, index) => (
          <p key={index}>{block || "\u00A0"}</p>
        ))}
      </div>
    </div>
  );
}

function TemplatesTray({
  templates,
  onInsert,
}: {
  templates: AiAssistantTemplate[];
  onInsert: (template: AiAssistantTemplate) => void;
}) {
  if (templates.length === 0) {
    return null;
  }
  return (
    <div className="ai-templates">
      {templates.map((template) => (
        <button
          key={template.id}
          type="button"
          className="ai-template-chip"
          onClick={() => onInsert(template)}
        >
          {template.label}
        </button>
      ))}
    </div>
  );
}

export function AiAssistantTab(): JSX.Element {
  const [selectedConversationId, setSelectedConversationId] = useState<string | undefined>(undefined);
  const [isStartingNew, setIsStartingNew] = useState<boolean>(false);
  const [messageDraft, setMessageDraft] = useState<string>("");
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery<AiAssistantDashboard, Error>({
    queryKey: ["ai-assistant", selectedConversationId ?? "default"],
    queryFn: () =>
      fetchAssistantDashboard({
        conversationId: selectedConversationId,
        roleId: DEFAULT_ROLE_ID,
        planId: DEFAULT_PLAN_ID,
      }),
    placeholderData: (previous) => previous,
  });

  useEffect(() => {
    if (!isStartingNew && !selectedConversationId && data?.conversations?.length) {
      setSelectedConversationId(data.conversations[0].id);
    }
  }, [isStartingNew, selectedConversationId, data?.conversations]);

  useEffect(() => {
    if (isStartingNew) {
      return;
    }
    const messageCount = data?.messages?.length ?? 0;
    if (messageCount > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isStartingNew, data?.messages?.length]);

  const permissionProfile: PermissionProfile | undefined = data?.permissionProfile;
  const effectiveRoleId = permissionProfile?.roleId ?? DEFAULT_ROLE_ID;
  const effectivePlanId = permissionProfile?.planId ?? DEFAULT_PLAN_ID;

  const sendMutation = useMutation({
    mutationFn: (content: string) =>
      sendAssistantMessage({
        conversationId: isStartingNew ? null : selectedConversationId ?? null,
        message: content,
        roleId: effectiveRoleId,
        planId: effectivePlanId,
      }),
    onSuccess: (response) => {
      setIsStartingNew(false);
      setSelectedConversationId(response.conversation.id);
      setMessageDraft("");

      const defaultKey = ["ai-assistant", "default"] as const;
      const responseKey = ["ai-assistant", response.conversation.id] as const;
      const currentDefault = queryClient.getQueryData<AiAssistantDashboard>(defaultKey) ?? data;

      const mergedConversations = [
        response.conversation,
        ...(currentDefault?.conversations ?? []),
      ].filter((conversation, index, self) => self.findIndex((candidate) => candidate.id === conversation.id) === index);

      const updatedDashboard: AiAssistantDashboard = {
        conversations: mergedConversations,
        messages: response.messages,
        templates: currentDefault?.templates ?? [],
        summary: {
          ...(currentDefault?.summary ?? EMPTY_SUMMARY),
          totalConversations: mergedConversations.length,
          activeConversations: Math.max(1, mergedConversations.length),
          lastInteractionAt: response.assistantMessage.createdAt,
        },
        lastRefreshedAt: response.generatedAt,
        permissionProfile: response.permissionProfile,
        performanceSnapshot: currentDefault?.performanceSnapshot ?? null,
      };

      queryClient.setQueryData(responseKey, updatedDashboard);
      queryClient.setQueryData(defaultKey, updatedDashboard);

      queryClient.invalidateQueries({ queryKey: ["ai-assistant"] });
    },
  });

  const conversations: AiAssistantDashboard["conversations"] = data?.conversations ?? [];
  const messages: AiAssistantMessage[] = isStartingNew ? [] : (data?.messages ?? []);
  const templates: AiAssistantDashboard["templates"] = data?.templates ?? [];
  const summary = data?.summary;
  const performanceSnapshot = data?.performanceSnapshot;

  const handleSelectConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setIsStartingNew(false);
  };

  const handleStartNewConversation = () => {
    setIsStartingNew(true);
    setSelectedConversationId(undefined);
    setMessageDraft("");
  };

  const handleInsertTemplate = (template: AiAssistantTemplate) => {
    setMessageDraft((prev) => {
      if (!prev) {
        return template.content;
      }
      return `${prev}\n\n${template.content}`;
    });
  };

  const handleSubmitMessage = () => {
    const trimmed = messageDraft.trim();
    if (!trimmed || sendMutation.isPending) {
      return;
    }
    sendMutation.mutate(trimmed);
  };

  const handleComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmitMessage();
    }
  };

  const composerPlaceholder = isStartingNew
    ? "Start a new conversation..."
    : "Ask the assistant... e.g. \"Summarise today's execution quality for BTC strategy.\"";

  return (
    <div className="ai-assistant-root">
      <section className="ai-chat">
        <header className="ai-chat__header">
          <div>
            <h2>AI Assistant</h2>
            <p>Ask about market posture, strategy tuning, or execution diagnostics.</p>
          </div>
          {summary ? (
            <div className="ai-chat__actions">
              <Card className="ai-inline-card" title={`${summary.totalConversations} threads`} subtitle="All time" />
              <Card
                className="ai-inline-card"
                title={`${summary.activeConversations} active`}
                subtitle={summary.lastInteractionAt ? `Last update ${formatRelativeTime(summary.lastInteractionAt)}` : "No activity"}
              />
            </div>
          ) : null}
        </header>

        <div className="ai-conversation">
          {isError && !data ? (
            <div className="ai-error">Failed to load assistant dashboard: {error.message}</div>
          ) : isLoading && !data ? (
            <Skeleton style={{ height: "240px" }} />
          ) : isStartingNew ? (
            <div className="ai-hint">Compose a message to start a new secure conversation with the assistant.</div>
          ) : !messages || messages.length === 0 ? (
            <div className="ai-hint">
              No messages in this thread yet. Ask a question to get context-aware guidance from the trading brain.
            </div>
          ) : (
            <div className="ai-messages">
              {messages.map((message, idx) => (
                <MessageBubble key={message.id || `msg-${idx}`} message={message} />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        <TemplatesTray templates={templates} onInsert={handleInsertTemplate} />

        <footer className="ai-composer">
          <div className="ai-composer__input">
            <textarea
              value={messageDraft}
              onChange={(event) => setMessageDraft(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              placeholder={composerPlaceholder}
              aria-label="Compose a message for the assistant"
              disabled={sendMutation.isPending}
            />
            <button
              type="button"
              className="ai-send-inline"
              aria-label="Send message"
              onClick={handleSubmitMessage}
              disabled={sendMutation.isPending || !messageDraft.trim()}
            >
              {sendMutation.isPending ? "…" : "➤"}
            </button>
          </div>
          {sendMutation.isError ? (
            <div className="ai-error">{(sendMutation.error as Error).message}</div>
          ) : null}
          {isFetching && !isStartingNew ? (
            <div className="ai-spinner" aria-live="polite">
              Refreshing assistant context...
            </div>
          ) : null}
        </footer>
      </section>

      <div className="ai-panes-container">
        <aside className="ai-pane">
          <div className="ai-pane__header">
            <div>
              <h3>Conversations</h3>
              <span>Threads with the trading co-pilot.</span>
            </div>
            <Button type="button" variant="primary" size="sm" onClick={handleStartNewConversation}>
              New thread
            </Button>
          </div>

          <div className="ai-pane__actions">
            <Button type="button" variant="ghost" size="sm" disabled>
              Insert system prompt
            </Button>
            <Button type="button" variant="ghost" size="sm" disabled>
              View run log
            </Button>
          </div>

          <div className="ai-scrollable ai-conversation-list">
            {isLoading && !data ? (
              <Skeleton style={{ height: "120px" }} />
            ) : conversations.length === 0 ? (
              <div className="ai-empty">No conversations yet. Start a new thread to begin.</div>
            ) : (
              conversations.map((conversation) => {
                const isActive = !isStartingNew && conversation.id === selectedConversationId;
                const summaryPreview =
                  conversation.summary?.split("\n").filter(Boolean)[0] ?? "";
                return (
                  <button
                    type="button"
                    key={conversation.id}
                    className={`ai-conversation-item ${isActive ? "ai-conversation-item--active" : ""}`}
                    onClick={() => handleSelectConversation(conversation.id)}
                  >
                    <div className="ai-conversation-item__title">{conversation.title}</div>
                    <div className="ai-conversation-item__meta">
                      <span>{formatRelativeTime(conversation.lastMessageAt)}</span>
                      {summaryPreview ? <span>Summary</span> : null}
                    </div>
                    {summaryPreview ? (
                      <div className="ai-conversation-item__summary">{summaryPreview}</div>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <aside className="ai-pane">
          <div className="ai-pane__header">
            <div>
              <h3>Assistant context</h3>
              <span>Permissions and recent performance.</span>
            </div>
          </div>

          <div className="ai-pane__section">
            <h4>Permissions</h4>
            {permissionProfile ? (
              <dl className="ai-permission-grid">
                <div>
                  <dt>Role</dt>
                  <dd>{permissionProfile.roleLabel ?? permissionProfile.roleId}</dd>
                </div>
                <div>
                  <dt>Plan</dt>
                  <dd>{permissionProfile.planLabel ?? permissionProfile.planId}</dd>
                </div>
                <div>
                  <dt>Authority</dt>
                  <dd>{permissionProfile.authorityLevel}</dd>
                </div>
                <div>
                  <dt>Automation mode</dt>
                  <dd>{permissionProfile.automationMode}</dd>
                </div>
              </dl>
            ) : (
              <div className="ai-empty">Permission data is loading…</div>
            )}
          </div>

          <div className="ai-pane__section">
            <h4>Performance snapshot</h4>
            {performanceSnapshot ? (
              <div className="ai-metric-grid">
                <div className="ai-metric">
                  <span>Window</span>
                  <strong>{performanceSnapshot.periodHours}h</strong>
                </div>
                {performanceSnapshot.metrics.slice(0, 3).map((metric) => (
                  <div key={metric.id} className="ai-metric">
                    <span>{metric.label}</span>
                    <strong>
                      {metric.unit === "%"
                        ? `${metric.value.toFixed(1)}%`
                        : metric.value.toFixed(2)}
                    </strong>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ai-empty">Performance data not available yet.</div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

